/**
 * PendingChangesBanner — small "N draft change(s) pending" banner (PIM Wave
 * P5 Task 7). Injected right after `<AdminPageHeader>` on the P1 direct-write
 * pages (`FieldLibraryPage`, `CategoryListPage`, `BindingEditorPage`,
 * `NeedMappingsPage`) so an admin who lands there via a bookmark/nav click
 * still notices an in-flight changeset before making an UNRELATED direct
 * edit alongside it.
 *
 * Queries `GET /admin/catalog/changesets?status=draft` — a lightweight count,
 * not the full drawer detail. Renders nothing (not an error state) in TWO
 * cases: the `pim.changesets` feature flag is off (the list call 404s
 * `pim.changeset.disabled`) or there are simply zero drafts right now. A
 * transient network error also renders nothing here — this is a secondary,
 * ambient affordance on pages whose OWN primary content has its own error
 * handling; a banner is not worth surfacing a scary error state for.
 */
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { FileDiff } from "lucide-react";

import { ADMIN_NS, registerAdminI18n } from "@/features/admin/api";
import { useChangesets } from "@/features/admin/changesets/hooks";

export function PendingChangesBanner(): ReactElement | null {
  registerAdminI18n();
  const { t } = useTranslation(ADMIN_NS);
  const { data, isError } = useChangesets("draft");

  if (isError) return null;
  const drafts = data?.changesets ?? [];
  if (drafts.length === 0) return null;

  return (
    <div
      data-testid="pim-pending-changes-banner"
      className="flex flex-wrap items-center justify-between gap-3 rounded-[var(--g-radius)] border border-[var(--g-primary)]/25 bg-[var(--g-accent-soft)] px-4 py-3"
    >
      <div className="flex items-center gap-2 text-sm text-[var(--g-primary-ink)]">
        <FileDiff size={16} strokeWidth={1.75} aria-hidden />
        <span>{t("pim.changesets.banner.message", { count: drafts.length })}</span>
      </div>
      <Link
        to="/admin/changesets"
        data-testid="pim-pending-changes-banner-link"
        className="text-sm font-medium text-[var(--g-primary)] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)] rounded-[var(--g-radius-sm)]"
      >
        {t("pim.changesets.banner.cta")}
      </Link>
    </div>
  );
}

export default PendingChangesBanner;
